import './ChannelDrawer.scss';
import { useEffect, useMemo, useState } from 'react';
import { Offcanvas } from '@Components/Offcanvas';
import { downloadPNG, exportMultiSheetToExcel } from '@src/utils/download-utils';
import { DashboardData, HierarchyLevel, HierarchyLevelConst } from '../../dtc-commerce-mmm-types';
import { useDtcCommerceMmmData } from '../../context/DtcCommerceMmmDataContext';
import {
  buildSubChannelColorMap,
  resolveChannelColor
} from '../../utils/dtc-commerce-mmm-channel-colors';
import {
  formatDrawerDateRange,
  subChannelDisplayName
} from '../../utils/dtc-commerce-mmm-channel-helpers';
import {
  shapeDrawerDecayExport,
  shapeDrawerInterchannelExport,
  shapeDrawerSaturationExport,
  shapeDrawerTrendExport
} from '../../utils/dtc-commerce-mmm-export';
import {
  formatCurrency,
  formatDollarsExact,
  formatWholeNumber
} from '../../utils/dtc-commerce-mmm-format';
import { ChannelIcon } from '../ChannelIcon';
import { MediaTypeBadge } from '../MediaTypeBadge';
import { EXPAND_BUTTON_CLASS } from './channel-drawer-constants';
import { deriveDrawerData } from './channel-drawer-data';
import { pngSelectorFor } from './channel-drawer-utils';
import { ChannelDrawerAttributionFlow } from './ChannelDrawerAttributionFlow';
import { ChannelDrawerDecay } from './ChannelDrawerDecay';
import { ChannelDrawerSaturation } from './ChannelDrawerSaturation';
import { ChannelDrawerSubChannels } from './ChannelDrawerSubChannels';
import { ChannelDrawerTimeSeries } from './ChannelDrawerTimeSeries';
import { SubChannelInfo, TSPoint } from './channel-drawer-types';

interface ChannelDrawerProps {
  data: DashboardData;
  selectedGroupKey: string | null;
  clickedLevel: HierarchyLevel;
  focusedFeatureName: string | null;
  onClose: () => void;
}

const formatEfficiency = (val: number | null): string => formatDollarsExact(val);

const buildInitialFocus = (
  clickedLevel: HierarchyLevel,
  clickedGroupKey: string,
  subChannels: SubChannelInfo[],
  focusedFeatureName: string | null
): Set<string> => {
  if (subChannels.length <= 1) return new Set();
  if (focusedFeatureName !== null) {
    const exists = subChannels.some(sc => sc.name === focusedFeatureName);
    return exists ? new Set([focusedFeatureName]) : new Set();
  }
  if (clickedLevel === HierarchyLevelConst.CHANNEL) return new Set();
  let matched: string[];
  if (clickedLevel === HierarchyLevelConst.BRAND_NONBRAND) {
    matched = subChannels.filter(sc => sc.name === clickedGroupKey).map(sc => sc.name);
  } else {
    const parts = clickedGroupKey.split('|');
    if (clickedLevel === HierarchyLevelConst.PLATFORM) {
      matched = subChannels.filter(sc => sc.platform === parts[1]).map(sc => sc.name);
    } else if (clickedLevel === HierarchyLevelConst.TACTIC) {
      matched = subChannels
        .filter(sc => sc.platform === parts[1] && sc.tactic === parts[2])
        .map(sc => sc.name);
    } else {
      matched = [];
    }
  }
  if (matched.length >= subChannels.length) return new Set();
  return new Set(matched);
};

export const ChannelDrawer = ({
  data,
  selectedGroupKey,
  clickedLevel,
  focusedFeatureName,
  onClose
}: ChannelDrawerProps) => {
  const { attributionType, dateRange, buildExportFilename } = useDtcCommerceMmmData();
  const isOpen = selectedGroupKey !== null;
  const isRevenue = data.modelInfo.kpiType === 'revenue';
  const attributedData = data[attributionType];

  const drawerData = useMemo(() => {
    if (selectedGroupKey === null) return null;
    return deriveDrawerData({
      data,
      attributedData,
      selectedGroupKey,
      clickedLevel,
      isRevenue
    });
  }, [data, attributedData, selectedGroupKey, clickedLevel, isRevenue]);

  const [focusedNames, setFocusedNames] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (drawerData === null || selectedGroupKey === null) return;
    setFocusedNames(
      buildInitialFocus(clickedLevel, selectedGroupKey, drawerData.subChannels, focusedFeatureName)
    );
  }, [drawerData, selectedGroupKey, clickedLevel, focusedFeatureName]);

  if (!isOpen || drawerData === null) return null;

  const {
    parentChannelKey,
    parentDisplayName,
    parentMediaType,
    parentIsDark,
    subChannels,
    channelTimeSeries,
    subChannelTimeSeries,
    saturationData,
    decayData,
    totalSpend,
    totalEffect,
    totalEfficiency
  } = drawerData;

  const hasFocus = focusedNames.size > 0;
  const canFocus = subChannels.length > 1;
  const parentColor = resolveChannelColor(parentChannelKey, data.channelColors);
  const channelColors = buildSubChannelColorMap(
    parentColor,
    subChannels.map(sc => sc.name)
  );

  const toggleFocus = (name: string) => {
    setFocusedNames(prev => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      if (next.size >= subChannels.length) return new Set();
      return next;
    });
  };
  const clearFocus = () => setFocusedNames(new Set());

  const focusedLabel = (() => {
    if (!hasFocus) return undefined;
    const labels = Array.from(focusedNames).map(subChannelDisplayName);
    if (labels.length <= 2) return labels.join(' & ');
    return `${labels.length} sub-channels`;
  })();

  const focusedTimeSeries: TSPoint[] = (() => {
    if (!hasFocus) return channelTimeSeries;
    const byDate = new Map<string, TSPoint>();
    Array.from(focusedNames).forEach(name => {
      const ts = subChannelTimeSeries.get(name) ?? [];
      ts.forEach(pt => {
        const cur = byDate.get(pt.date) ?? { date: pt.date, spend: 0, contribution: 0 };
        cur.spend += pt.spend;
        cur.contribution += pt.contribution;
        byDate.set(pt.date, cur);
      });
    });
    return Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
  })();

  const focusedSubChannelNames = hasFocus
    ? Array.from(focusedNames)
    : subChannels.map(sc => sc.name);

  // Each drawer view gets its own download dropdown. Exports always cover the
  // full set of sub-channels for the parent — focused state in the UI doesn't
  // narrow the download.
  const fileFor = (visual: string) =>
    buildExportFilename(data.modelInfo.client, visual, parentChannelKey);
  // Strip the download dropdown and expand button from the PNG snapshot so they
  // don't overlay the chart in the exported image.
  const PNG_STRIP_CLASSES = ['.download', `.${EXPAND_BUTTON_CLASS}`];
  const pngFor =
    (chartClass: string, visual: string) =>
    (isExpanded = false) =>
      downloadPNG(pngSelectorFor(chartClass, isExpanded), fileFor(visual), '', PNG_STRIP_CLASSES);
  const trendDownload = {
    onExcel: () => exportMultiSheetToExcel(shapeDrawerTrendExport(drawerData), fileFor('trend')),
    onPng: pngFor('.channel-drawer-time-series', 'trend')
  };
  const saturationDownload = {
    onExcel: () =>
      exportMultiSheetToExcel(shapeDrawerSaturationExport(drawerData), fileFor('saturation')),
    onPng: pngFor('.channel-drawer-saturation', 'saturation')
  };
  const decayDownload = {
    onExcel: () => exportMultiSheetToExcel(shapeDrawerDecayExport(drawerData), fileFor('decay')),
    onPng: pngFor('.channel-drawer-decay', 'decay')
  };
  const interchannelDownload = {
    onExcel: () =>
      exportMultiSheetToExcel(
        shapeDrawerInterchannelExport(drawerData, data),
        fileFor('interchannel')
      ),
    onPng: pngFor('.channel-drawer-attribution-flow', 'interchannel')
  };

  const kpiName = isRevenue ? 'Revenue' : 'Conversions';
  const fmtKpi = isRevenue ? formatCurrency : formatWholeNumber;
  const efficiencyLabel = isRevenue ? 'ROAS' : 'CPA';
  const efficiencyDisplay = formatEfficiency(totalEfficiency);
  const ciPct =
    data.modelInfo.confidenceLevel > 0 ? Math.round(data.modelInfo.confidenceLevel * 100) : null;

  const header = (
    <div className='channel-drawer-header-content'>
      <div className='channel-drawer-header-top'>
        <div className='channel-drawer-header-row'>
          <h2 className='channel-drawer-title'>
            <ChannelIcon channel={parentChannelKey} level={HierarchyLevelConst.CHANNEL} size={24} />
            {parentDisplayName}
          </h2>
          <span className='channel-drawer-header-date'>
            {formatDrawerDateRange(dateRange.startDate, dateRange.endDate)}
          </span>
        </div>
        <div className='channel-drawer-header-row'>
          <div className='channel-drawer-header-chips'>
            <MediaTypeBadge mediaType={parentMediaType} />
            {parentIsDark && <span className='channel-drawer-header-meta'>Dark channel</span>}
            {subChannels.length > 1 && (
              <span className='channel-drawer-header-meta'>{subChannels.length} sub-channels</span>
            )}
          </div>
          <span
            className={`channel-drawer-header-attr ${attributionType === 'actualized' ? 'is-actualized' : ''}`}
          >
            {attributionType === 'actualized' ? 'Actualized' : 'Direct'}
          </span>
        </div>
      </div>
      <div className='channel-drawer-header-metrics'>
        <div className='channel-drawer-metric'>
          <div className='channel-drawer-metric-label'>Spend</div>
          <div className='channel-drawer-metric-value'>{formatCurrency(totalSpend)}</div>
        </div>
        <div className='channel-drawer-metric'>
          <div className='channel-drawer-metric-label'>{kpiName}</div>
          <div className='channel-drawer-metric-value'>{fmtKpi(totalEffect)}</div>
        </div>
        <div className='channel-drawer-metric'>
          <div className='channel-drawer-metric-label'>{efficiencyLabel}</div>
          <div className='channel-drawer-metric-value'>{efficiencyDisplay}</div>
        </div>
      </div>
    </div>
  );

  return (
    <Offcanvas
      show={isOpen}
      onHide={onClose}
      placement='end'
      width='600px'
      header={header}
      ariaLabel={`${parentDisplayName} channel details`}
      className='channel-drawer'
    >
      <div className='channel-drawer-content'>
        <ChannelDrawerSubChannels
          subChannels={subChannels}
          isRevenue={isRevenue}
          focusedNames={focusedNames}
          canFocus={canFocus}
          onToggleFocus={toggleFocus}
          onClearFocus={clearFocus}
        />
        <ChannelDrawerTimeSeries
          data={focusedTimeSeries}
          isRevenue={isRevenue}
          focusedLabel={focusedLabel}
          revenueColor={parentColor}
          download={trendDownload}
        />
        <ChannelDrawerSaturation
          data={saturationData}
          focusedNames={hasFocus ? focusedNames : null}
          isRevenue={isRevenue}
          dateRangeLabel={dateRange.label}
          focusedLabel={focusedLabel}
          channelColors={channelColors}
          ciPct={ciPct}
          download={saturationDownload}
        />
        <ChannelDrawerDecay
          data={decayData}
          focusedNames={hasFocus ? focusedNames : null}
          focusedLabel={focusedLabel}
          channelColors={channelColors}
          ciPct={ciPct}
          download={decayDownload}
        />
        <ChannelDrawerAttributionFlow
          data={data}
          subChannelNames={focusedSubChannelNames}
          parentChannelKey={parentChannelKey}
          isRevenue={isRevenue}
          download={interchannelDownload}
        />
      </div>
    </Offcanvas>
  );
};
